import { useLazyQuery } from '@apollo/client'
import AccountBox from '@mui/icons-material/AccountBox'
import HourglassEmpty from '@mui/icons-material/HourglassEmpty'
import PersonAdd from '@mui/icons-material/PersonAdd'
import Search from '@mui/icons-material/Search'
import { Grid } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { Query } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

import RequestCampaignsDialog from '@src/components/content/RequestCampaignsDialog'
import LegacyRequestMoreCampaignsDialog from '@src/components/content/RequestMoreCampaignsDialog'
import FilterContentLayoutWrapper from '@src/components/shared/FilterContentLayoutWrapper'
import booleanFilter from '@src/lib/booleanFilter'
import ChildSpacer from '@src/lib/ChildSpacer'
import LinkChild from '@src/lib/LinkChild'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import WhatIsThis from '@src/lib/WhatisThis'

import { Breadcrumb, BreadcrumbMenu, Breadcrumbs } from '../shared/Breadcrumbs'

import AddBrands from './AddBrands'
import ManageBrands from './ManageBrands'
import manageBrandsPaths from './manageBrandsPaths'
import PendingInvites from './PendingInvites'
import PendingInvitesChip, { GET_PENDING_INVITES } from './PendingInvitesChip'
import SuggestBrands from './SuggestBrands'

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(6),
  },
  chipContainer: {
    paddingLeft: theme.spacing(2),
  },
  flexGrow: {
    flexGrow: 1,
  },
  tooltipZIndex: {
    zIndex: theme.zIndex.modal - 1,
  },
  pendingInvitesButton: {
    'backgroundColor': theme.palette.error.light,
    'color': theme.palette.error.contrastText,
    'marginTop': theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
    '&:active': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  featureDescriptionMobile: {
    [theme.breakpoints.only('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  titleAndButtons: {
    marginTop: theme.spacing(1),
  },
}))

const ManageBrandsLayout: React.FC<React.PropsWithChildren> = () => {
  const useNewContentRequests = useFeatureFlag('new-content-requests')
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false)
  const { classes } = useStyles()
  const params = manageBrandsPaths.index.useParams()
  const isPendingInvitesEnabled = useFeatureFlag('accept-pending-invites')
  const useNewFilters = useFeatureFlag('improved-brand-filtering')
  const currentUser = useSelector((state) => state.auth.user)
  const [runQuery, { data, loading }] = useLazyQuery<Query>(GET_PENDING_INVITES)

  useEffect(() => {
    runQuery({
      variables: {
        userId: currentUser.id,
      },
    })
  }, [currentUser.id, runQuery])

  let selectedTab: string
  let children: JSX.Element | null = null
  switch (params.action) {
    case 'suggest':
      selectedTab = 'suggest'
      children = <SuggestBrands />
      break
    case 'add':
      selectedTab = 'add'
      children = <AddBrands />
      break
    case 'pending-invites':
      selectedTab = 'pending-invites'
      children = <PendingInvites />
      break
    default:
      selectedTab = 'manage'
      children = <ManageBrands />
      break
  }

  const props = {
    title: I18n.t('manageBrands.manageBrands'),
    slotProps: {
      tab: {
        selectedTab,
        tabs: booleanFilter([
          {
            value: 'manage',
            label: I18n.t('manageBrands.yourBrands'),
            to: manageBrandsPaths.index.build({
              retailerAccountSlug: params.retailerAccountSlug,
              brandSlug: params.brandSlug,
            }),
          },
          isPendingInvitesEnabled
            ? {
                value: 'pending-invites',
                label: (
                  <span>
                    <ChildSpacer>
                      {I18n.t('manageBrands.pendingInvites')}
                      <PendingInvitesChip />
                    </ChildSpacer>
                  </span>
                ),
                to: manageBrandsPaths.index.build({
                  brandSlug: params.brandSlug,
                  retailerAccountSlug: params.retailerAccountSlug,
                  action: 'pending-invites',
                }),
              }
            : undefined,
          {
            value: 'add',
            label: I18n.t('manageBrands.addBrands'),
            to: manageBrandsPaths.index.build({
              retailerAccountSlug: params.retailerAccountSlug,
              brandSlug: params.brandSlug,
              action: 'add',
            }),
          },
          {
            value: 'suggest',
            label: I18n.t('manageBrands.suggestBrands'),
            to: manageBrandsPaths.index.build({
              retailerAccountSlug: params.retailerAccountSlug,
              brandSlug: params.brandSlug,
              action: 'suggest',
            }),
          },
        ]),
      },
      breadCrumb: {
        to: manageBrandsPaths.index.build({
          retailerAccountSlug: params.retailerAccountSlug,
          brandSlug: params.brandSlug,
        }),
      },
      breadCrumbMenu: {
        selected: selectedTab,
        items: booleanFilter([
          {
            Icon: AccountBox,
            label: I18n.t('manageBrands.yourBrands'),
            key: 'manage',
            to: manageBrandsPaths.index.build({
              retailerAccountSlug: params.retailerAccountSlug,
              brandSlug: params.brandSlug,
              // action: 'foobar',
            }),
          },
          isPendingInvitesEnabled
            ? {
                Icon: HourglassEmpty,
                label: I18n.t('manageBrands.pendingInvites'),
                key: 'pending-invites',
                to: manageBrandsPaths.index.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                  brandSlug: params.brandSlug,
                  action: 'pending-invites',
                }),
                value: 'pending-invites',
              }
            : undefined,
          {
            Icon: PersonAdd,
            label: I18n.t('manageBrands.addBrands'),
            key: 'add',
            to: manageBrandsPaths.index.build({
              retailerAccountSlug: params.retailerAccountSlug,
              brandSlug: params.brandSlug,
              action: 'add',
            }),
            value: 'add',
          },
          {
            Icon: Search,
            label: I18n.t('manageBrands.suggestBrands'),
            key: 'suggest',
            to: manageBrandsPaths.index.build({
              retailerAccountSlug: params.retailerAccountSlug,
              brandSlug: params.brandSlug,
              action: 'suggest',
            }),
          },
        ]),
      },
    },
  }
  return useNewFilters ? (
    <FilterContentLayoutWrapper {...props}>
      <Grid
        alignItems="center"
        container
        spacing={2}
        className={classes.titleAndButtons}
        sx={{ marginY: 2 }}
      >
        <Grid item xs={12} sm={5} md={6}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <WhatIsThis
              item={`manageBrands.whatIsThis.${selectedTab}`}
              TooltipProps={{
                PopperProps: {
                  className: classes.tooltipZIndex,
                },
              }}
            />
            <Typography
              variant="body1"
              className={classes.featureDescriptionMobile}
            >
              {I18n.t('manageBrands.featureDescription')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <Button
            onClick={() => setIsRequestDialogOpen(true)}
            variant="contained"
            color="primary"
            fullWidth
          >
            {I18n.t('content.requestMore')}
          </Button>
        </Grid>

        {isPendingInvitesEnabled && (
          <Grid item xs={12} sm={3}>
            <LinkChild
              to={manageBrandsPaths.index.build({
                brandSlug: 'all',
                retailerAccountSlug: params.retailerAccountSlug,
                action: 'pending-invites',
              })}
              className={classes.pendingInvitesButton}
              style={{ marginTop: 0 }}
            >
              <Button
                variant="contained"
                disabled={!data?.getUser?.registration_keys?.length}
                fullWidth
              >
                {I18n.t('manageBrands.pendingInvitesWithCount', {
                  count: loading
                    ? 0
                    : data?.getUser?.registration_keys?.length || 0,
                })}
              </Button>
            </LinkChild>
          </Grid>
        )}
      </Grid>

      {children}
      {useNewContentRequests ? (
        <RequestCampaignsDialog
          brandId={null}
          open={isRequestDialogOpen}
          onClose={() => setIsRequestDialogOpen(false)}
        />
      ) : (
        <LegacyRequestMoreCampaignsDialog
          brandId={null}
          open={isRequestDialogOpen}
          onClose={() => setIsRequestDialogOpen(false)}
        />
      )}
    </FilterContentLayoutWrapper>
  ) : (
    <div className={classes.root}>
      <Breadcrumbs pb={4}>
        <Breadcrumb {...props.slotProps.breadCrumb}>
          {I18n.t('manageBrands.manageBrands')}
        </Breadcrumb>
        <BreadcrumbMenu {...props.slotProps.breadCrumbMenu} />
      </Breadcrumbs>
      <Divider />
      <Grid
        alignItems="center"
        container
        spacing={2}
        className={classes.titleAndButtons}
      >
        <Grid item xs={12} sm={9}>
          <Typography variant="h5">
            {I18n.t('manageBrands.manageBrands')}
          </Typography>
        </Grid>
        <div className={classes.flexGrow} />
        <Grid item xs={12} sm={3}>
          <Button
            onClick={() => setIsRequestDialogOpen(true)}
            variant="contained"
            color="primary"
            fullWidth
          >
            {I18n.t('content.requestMore')}
          </Button>
          {isPendingInvitesEnabled && (
            <>
              <br />
              <LinkChild
                to={manageBrandsPaths.index.build({
                  brandSlug: params.brandSlug,
                  retailerAccountSlug: params.retailerAccountSlug,
                  action: 'pending-invites',
                })}
                className={classes.pendingInvitesButton}
              >
                <Button
                  variant="contained"
                  disabled={!data?.getUser?.registration_keys?.length}
                  fullWidth
                >
                  {I18n.t('manageBrands.pendingInvitesWithCount', {
                    count: loading
                      ? 0
                      : data?.getUser?.registration_keys?.length || 0,
                  })}
                </Button>
              </LinkChild>
            </>
          )}
        </Grid>
      </Grid>

      <Typography
        variant="body1"
        gutterBottom
        className={classes.featureDescriptionMobile}
      >
        {I18n.t('manageBrands.featureDescription')}
      </Typography>
      <Box display="flex">
        <div>
          <WhatIsThis
            item={`manageBrands.whatIsThis.${selectedTab}`}
            TooltipProps={{
              PopperProps: {
                className: classes.tooltipZIndex,
              },
              placement: 'bottom-start',
            }}
          />
        </div>
        <Tabs value={selectedTab}>
          <Tab
            value={'manage'}
            label={I18n.t('manageBrands.yourBrands')}
            component={Link}
            to={manageBrandsPaths.index.build({
              brandSlug: params.brandSlug,
              retailerAccountSlug: params.retailerAccountSlug,
            })}
          />
          {isPendingInvitesEnabled ? (
            <Tab
              value={'pending-invites'}
              label={
                <span>
                  <ChildSpacer>
                    {I18n.t('manageBrands.pendingInvites')}
                    <PendingInvitesChip />
                  </ChildSpacer>
                </span>
              }
              component={Link}
              to={manageBrandsPaths.index.build({
                brandSlug: params.brandSlug,
                retailerAccountSlug: params.retailerAccountSlug,
                action: 'pending-invites',
              })}
            />
          ) : undefined}
          <Tab
            value={'add'}
            label={I18n.t('manageBrands.addBrands')}
            component={Link}
            to={manageBrandsPaths.index.build({
              brandSlug: params.brandSlug,
              retailerAccountSlug: params.retailerAccountSlug,
              action: 'add',
            })}
          />
          <Tab
            value={'suggest'}
            label={I18n.t('manageBrands.suggestBrands')}
            component={Link}
            to={manageBrandsPaths.index.build({
              brandSlug: params.brandSlug,
              retailerAccountSlug: params.retailerAccountSlug,
              action: 'suggest',
            })}
          />
        </Tabs>
      </Box>
      <br />
      {children}
      {useNewContentRequests ? (
        <RequestCampaignsDialog
          brandId={null}
          open={isRequestDialogOpen}
          onClose={() => setIsRequestDialogOpen(false)}
        />
      ) : (
        <LegacyRequestMoreCampaignsDialog
          brandId={null}
          open={isRequestDialogOpen}
          onClose={() => setIsRequestDialogOpen(false)}
        />
      )}
    </div>
  )
}

export default ManageBrandsLayout
